/**
 * The retention policy the product offers, and the rule set the sweep reads (plan-002 RB-28,
 * design §15.6.8, P14-05, AC-35).
 *
 * ── ONE SOURCE FOR THE SCREEN AND THE SWEEP ────────────────────────────────────────────────
 * Design §15.6.8: `getCompliance` answers each class with "the stored row, or the product policy
 * value in `packages/domain`'s `RETENTION_POLICY_DAY` when none is stored; the sweeper reads the
 * same constant, so the screen and the sweeper cannot disagree." This module is that constant,
 * the closed option set per class, and {@link effectiveRetentionRule} — the one function both the
 * API route and the retention sweep turn stored rows into rules with. A class with no stored row
 * is never skipped and never guessed: it gets the product policy, stated here once.
 *
 * `RETENTION_POLICY_DAY` IS `RETENTION_DEFAULT_DAY`. `governance.ts` already states the defaults of
 * design §4.10; restating the numbers here would be a second copy that could drift, so this is
 * the same frozen object under the name the design gives it.
 *
 * ── A CHOICE IS PICKED FROM AN OFFER, NEVER TYPED ──────────────────────────────────────────
 * The classes offer different values on purpose (the prototype draws them): recordings 7, 30, 90
 * days or 12 months; orders and survey answers may be kept until the member deletes them
 * ({@link RETAIN_FOREVER}); caller details may be deleted with the conversation (`0`); the audit
 * log is fixed at 730 days and is not the member's to shorten. A free number has no sweep rule,
 * so {@link isRetentionOption} is the whole validation `putRetentionPolicy` applies.
 *
 * ── A PERSON-DATA REQUEST'S SEARCH TERM ────────────────────────────────────────────────────
 * Design §15.6.8: `search_term` is an E.164 number or an email address. The row keeps only a
 * hash; what the Compliance screen shows is a MASK written into the audit entry — the last four
 * digits of a number, or the first character and the domain of an address.
 * {@link readPersonDataSearchTerm} is the one reader, so the API and any later consumer agree on
 * what counts as a number, what counts as an address, and how each is masked. Hashing stays with
 * the caller (`e164HashOf` for a number, SHA-256 of the lower-cased address for email), because
 * this package runs in the browser too and carries no platform crypto.
 */
import { normalisedEmail } from "../identity/discovery.js";
import { RETENTION_DEFAULT_DAY } from "./governance.js";
import { RETAIN_FOREVER } from "./retention.js";
/** Every retention class, in the order the design lists them (§4.10). */
export const RETENTION_CLASS = Object.freeze([
    "recording",
    "transcript",
    // Quoted: the design's own enum value is the bare word (C14) — data, not an identifier.
    "order",
    "survey_result",
    "caller_detail",
    "audit",
]);
/**
 * The product policy: how long each class is kept when the workspace has chosen nothing.
 * The same object as `RETENTION_DEFAULT_DAY`; see this file's header.
 */
export const RETENTION_POLICY_DAY = RETENTION_DEFAULT_DAY;
/**
 * The closed option set per class. `-1` is {@link RETAIN_FOREVER} ("keep until I delete them");
 * `0` on caller details is "delete with the conversation". Every class's policy value is one of
 * its own options, which the suite asserts.
 */
export const RETENTION_OPTION_DAY = Object.freeze({
    recording: Object.freeze([7, 30, 90, 365]),
    transcript: Object.freeze([30, 365, 730]),
    "order": Object.freeze([365, 730, RETAIN_FOREVER]),
    survey_result: Object.freeze([365, 730, RETAIN_FOREVER]),
    caller_detail: Object.freeze([0, 365]),
    audit: Object.freeze([730]),
});
export function isRetentionClass(raw) {
    return RETENTION_CLASS.includes(raw);
}
/** Whether `retainDay` is one of the values `retentionClass` offers. Anything else is refused. */
export function isRetentionOption(retentionClass, retainDay) {
    return typeof retainDay === "number" && RETENTION_OPTION_DAY[retentionClass].includes(retainDay);
}
/**
 * The rule for EVERY class, from the rows a workspace has stored.
 *
 * A stored row wins, as stored — even one no longer in the class's option set, because the sweep
 * must honour what the member chose rather than silently moving it; the screen renders such a
 * value as the fault it is. A class with no row gets {@link RETENTION_POLICY_DAY}. Two rows for
 * one class cannot exist (`UNIQUE (account_id, workspace_id, class)`), and are refused here too
 * rather than resolved by whichever came last.
 */
export function effectiveRetentionRule(stored) {
    const chosen = new Map();
    for (const one of stored) {
        if (chosen.has(one.class)) {
            throw new RangeError(`Two retention_policy rows for class "${one.class}" in one workspace; the table's ` +
                `unique constraint forbids it, and no rule is guessed between them.`);
        }
        chosen.set(one.class, one.retainDay);
    }
    return RETENTION_CLASS.map((retentionClass) => {
        const found = chosen.get(retentionClass);
        return found === undefined
            ? { class: retentionClass, retainDay: RETENTION_POLICY_DAY[retentionClass], configured: false }
            : { class: retentionClass, retainDay: found, configured: true };
    });
}
// ---------------------------------------------------------------------------------------
// Person-data requests — the search term
// ---------------------------------------------------------------------------------------
const E164 = /^\+[1-9]\d{6,14}$/;
/**
 * Reads a person-data request's search term, or answers `null`.
 *
 * A number is accepted in E.164 after removing spaces, brackets and hyphens (a person types
 * `+30 210 123 4567`); it is not completed from a national format, because the region would be a
 * guess and a deletion is irreversible. An address is lower-cased and validated by the identity
 * module's strict reader. Anything else is `null` — never a best effort.
 */
export function readPersonDataSearchTerm(raw) {
    const trimmed = raw.trim();
    if (trimmed.includes("@")) {
        const address = normalisedEmail(trimmed);
        if (address === null)
            return null;
        const at = address.indexOf("@");
        return { kind: "email", address, masked: `${address.slice(0, 1)}···${address.slice(at)}` };
    }
    const e164 = trimmed.replace(/[\s()–-]/g, "");
    if (!E164.test(e164))
        return null;
    return { kind: "e164", e164, masked: `···${e164.slice(-4)}` };
}
//# sourceMappingURL=retention-policy.js.map